import React, { useContext, useEffect, useState } from 'react';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { useImmer } from 'use-immer';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Chip,
  TablePagination,
  Alert,
} from '@mui/material';
import BulkAction from '../../common/bulk-action/bulk-action';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import FilterBar from '../../common/filter-bar/filter-bar';
import SearchBar from '../../common/search-bar/search-bar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
  defaultDriverOrderFooter,
} from '../../common/report-builder/fields-config';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { PaginationContext } from '../../../contexts/pagination-context';
import {
  ActorFilterOption,
  DeliveryBatchStateFilterOption,
  IFilterOption,
} from '../../common/filter-bar/types';
import DeliveryBatchDialog from '../delivery-tasks/delivery-task-dialog';
import { SortingByColumnState } from '../../common/orders-listing-table/utils';
import { IDeliveryBatch } from '../../../interfaces/delivery-company/tasks';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { DeliveryBatchState } from '../../../enums/tasks';
import { useSnackBar } from '../../../contexts/snackbar';
import ResponsiveDeliveryBatchesListing, {
  delivery_batches_relevant_fields,
} from '../common/delivery-tasks-listing';
import TaskWaybillsDialog from '../delivery-tasks/tasks-listing/task-waybills-dialog';
import { DeliveryBatchFilterKeys } from '../../common/filter-bar/keys-config';

export interface DriverDeliveriesProps {
  driver: DriverType;
}

function DriverDeliveries({ driver }: DriverDeliveriesProps) {
  const { showMessage } = useSnackBar();
  const [cancellingTask, setCancellingTask] = useState<IDeliveryBatch>();
  const [completingTask, setCompletingTask] = useState<IDeliveryBatch>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [deliveryList, setDeliveryList] = useState<IDeliveryBatch[]>([]);
  const [tasksCount, setTasksCount] = useState(0);
  const [editingTask, setEditingTask] = useState<IDeliveryBatch>();
  const [downloadWaybillForTask, setDownloadWaybillForTask] =
    useState<IDeliveryBatch>();
  const [downloadReturnStatementsForTask, setDownloadReturnStatementsForTask] =
    useState<IDeliveryBatch>();
  const [downloadBatchQrcode, setDownloadBatchQrcode] =
    useState<IDeliveryBatch>();
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handleCancelTask = async () => {
    try {
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/delivery-batch/${cancellingTask?.id}/`;
      const response = await axios.delete(endpoint, config);
      setResponse(response);
      setDeliveryList(
        deliveryList.map((task) =>
          task.id === cancellingTask?.id
            ? { ...task, status: DeliveryBatchState.CANCELLED }
            : task
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        showMessage('Failed to cancel delivery request', 'error');
      } else {
        showMessage('Failed to cancel delivery request', 'error');
      }
    }
    setIsLoading(false);
  };

  const handleCompleteTask = async () => {
    try {
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/delivery-batch/${completingTask?.id}/complete/`;
      const response = await axios.post(endpoint, {}, config);
      setResponse(response);
      setDeliveryList(
        deliveryList.map((task) =>
          task.id === completingTask?.id
            ? { ...task, status: DeliveryBatchState.OUT_FOR_DELIVERY }
            : task
        )
      );
      showMessage("Batch marked as collected.")
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
      } 
      showMessage('Failed to mark batch as collected.', 'error');
    }
    setIsLoading(false);
  }

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const [filters, setFilters] = useState<IFilterOption[]>([
    new ActorFilterOption(
      {
        actortype: 'driver',
        val: driver,
      },
      true
    ),
    new DeliveryBatchStateFilterOption(DeliveryBatchState.ASSIGNED),
  ]);

  const [sorting, setSorting] = useState<SortingByColumnState>({
    column: 'creation_date',
    order: 'decreasing',
  });

  useEffect(() => {
    let response: any;
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/delivery-batches/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${delivery_batches_relevant_fields.join(',')}`;

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      endpoint += `&sort_by_${sorting.order}=${encodeURIComponent(sorting.column)}`;

      console.log(endpoint);
      try {
        response = await axios.get<{ count: number; results: OrderType[] }>(
          endpoint,
          config
        );
        setDeliveryList(response.data.results);
        setTasksCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [controller, dateFilter, filters, sorting]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
  };

  return (
    <Box>
      <Accordion elevation={0}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography
            variant="h5"
            component="h5"
            sx={{ textAlign: 'center', mt: 1, mb: 1 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <ManageSearchIcon fontSize="large" color="primary" />
              <span>
                <b>Search and Filter Controls</b>
              </span>
            </div>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DoubleDatePicker dateFilterAction={dateFilterAction} />

          <br />

          <FilterBar
            filterQueryAction={filterQueryAction}
            currentlySelectedFilters={filters}
            filterKeys={['delivery batch state']}
          />
        </AccordionDetails>
      </Accordion>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
          marginLeft: 10,
        }}
      ></div>

      <ResponsiveDeliveryBatchesListing
        tasksList={deliveryList}
        isLoading={isLoading}
        setEditingTask={setEditingTask}
        setDownloadBatchQrcode={setDownloadBatchQrcode}
        setDownloadWaybillForTask={setDownloadWaybillForTask}
        setCancellingTask={setCancellingTask}
        setCompletingTask={setCompletingTask}
        setDownloadReturnStatementsForTask={setDownloadReturnStatementsForTask}
      />

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={tasksCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
      {downloadWaybillForTask && (
        <TaskWaybillsDialog
          task={downloadWaybillForTask}
          onClose={() => setDownloadWaybillForTask(undefined)}
          mode={'orders'}
        />
      )}
      {downloadReturnStatementsForTask && (
        <TaskWaybillsDialog
          task={downloadReturnStatementsForTask}
          onClose={() => setDownloadReturnStatementsForTask(undefined)}
          mode={'returns'}
        />
      )}
      {downloadBatchQrcode && (
        <TaskWaybillsDialog
          task={downloadBatchQrcode}
          onClose={() => setDownloadBatchQrcode(undefined)}
          mode="batch"
        />
      )}
      {editingTask && (
        <DeliveryBatchDialog
          taskID={editingTask.id}
          onClose={() => setEditingTask(undefined)}
        />
      )}

      <ConfirmAction
        message={'Are you sure you want to cancel this task?'}
        action={handleCancelTask}
        isOpen={cancellingTask != undefined}
        handleClose={() => setCancellingTask(undefined)}
        isSevereAction={true}
        confirmSevereActionTarget={'cancel task'}
      >
        <Alert severity="warning">This action can not be undone.</Alert>
      </ConfirmAction>

       <ConfirmAction
        message={'Are you sure you want to mark this batch as collected?'}
        action={handleCompleteTask}
        isOpen={completingTask != undefined}
        handleClose={() => setCompletingTask(undefined)}
      >
        <Alert severity="warning">This action can not be undone.</Alert>
      </ConfirmAction>
    </Box>
  );
}

export default DriverDeliveries;
